import { Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, alpha, useTheme } from '@mui/material';
import type { HansAckerFinding, HansAckerSeverity } from './types';
import { PAGE_SIZE, filterFindings, paginate, severityColor, severityLabel } from './utils';

const FILTERS: { id: HansAckerSeverity | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'info', label: 'Info' },
];

interface Props {
  findings: HansAckerFinding[];
  severity: HansAckerSeverity | 'all';
  page: number;
  onSeverityChange: (s: HansAckerSeverity | 'all') => void;
  onPageChange: (p: number) => void;
  onOpen: (id: string) => void;
}

export function SeverityBadge({ severity }: { severity: HansAckerSeverity }) {
  const color = severityColor(severity);
  return (
    <Box component="span" sx={{ display: 'inline-block', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color, bgcolor: alpha(color, 0.14), borderRadius: 0.75, px: 1, py: 0.35 }}>
      {severity === 'info' ? 'Info' : severityLabel(severity)}
    </Box>
  );
}

const mono = { fontFamily: "'Consolas', monospace" };

export default function FindingsTable({ findings, severity, page, onSeverityChange, onPageChange, onOpen }: Props) {
  const theme = useTheme();
  const visible = filterFindings(findings, 'all', severity);
  const paged = paginate(visible, page);
  const head = { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' as const, color: 'text.disabled', py: 1 };

  return (
    <Box id="hans-acker-findings" sx={{ scrollMarginTop: 72, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, p: 2.5 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Findings</Typography>
        <Typography sx={{ ml: 'auto', fontSize: 12, color: 'text.disabled' }}>Click a row for evidence, reproduction steps and the suggested fix</Typography>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
        {FILTERS.map((f) => (
          <Chip
            key={f.id}
            label={f.label}
            size="small"
            clickable
            onClick={() => onSeverityChange(f.id)}
            sx={{
              fontWeight: 600, fontSize: 12,
              bgcolor: severity === f.id ? alpha(theme.palette.text.primary, 0.12) : alpha(theme.palette.text.primary, 0.04),
              color: severity === f.id ? 'text.primary' : 'text.secondary',
            }}
          />
        ))}
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ID', 'Severity', 'Title', 'Type', 'Folder', 'Location', 'CVSS', 'Confidence'].map((h) => <TableCell key={h} sx={head}>{h}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.items.map((f) => (
              <TableRow key={f.id} hover onClick={() => onOpen(f.id)} sx={{ cursor: 'pointer' }}>
                <TableCell sx={{ ...mono, fontSize: 12, whiteSpace: 'nowrap' }}>{f.id}</TableCell>
                <TableCell><SeverityBadge severity={f.severity} /></TableCell>
                <TableCell sx={{ fontSize: 13, minWidth: 260 }}>{f.doc.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={f.type === 'code' ? 'Code' : f.type === 'dep' ? 'Dependency' : 'Review'}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: alpha(f.type === 'code' ? '#a78bfa' : f.type === 'dep' ? '#2dd4bf' : '#94a3b8', 0.16), color: f.type === 'code' ? '#8b5cf6' : f.type === 'dep' ? '#0d9488' : '#64748b' }}
                  />
                </TableCell>
                <TableCell>
                  <Chip size="small" label={f.folder} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: alpha('#60a5fa', 0.16), color: '#3b82f6' }} />
                </TableCell>
                <TableCell sx={{ ...mono, fontSize: 11.5, color: 'text.secondary' }}>{f.location}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f.cvss ?? '\u2014'}</TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f.confidence}</TableCell>
              </TableRow>
            ))}
            {!visible.length && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.disabled' }}>No findings match the current filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {paged.pages > 1 && (
        <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center" sx={{ mt: 1.5 }} aria-label={`Page ${paged.page} of ${paged.pages}, ${PAGE_SIZE} per page`}>
          <Button size="small" disabled={paged.page === 1} onClick={() => onPageChange(paged.page - 1)} sx={{ textTransform: 'none', minWidth: 0 }}>{'\u2039'} Prev</Button>
          {Array.from({ length: paged.pages }, (_, i) => i + 1).map((p) => (
            <Button key={p} size="small" variant={p === paged.page ? 'contained' : 'text'} disableElevation onClick={() => onPageChange(p)} sx={{ textTransform: 'none', minWidth: 30 }}>{p}</Button>
          ))}
          <Button size="small" disabled={paged.page === paged.pages} onClick={() => onPageChange(paged.page + 1)} sx={{ textTransform: 'none', minWidth: 0 }}>Next {'\u203a'}</Button>
        </Stack>
      )}
    </Box>
  );
}
